import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Vertical Oceans — Step 2 subscription offer controller (multi-product).
 *
 * Progressive enhancement for <vo-cart-offer> (rendered by snippets/vo-cart-offer.liquid
 * + snippets/vo-cart-offer-card.liquid). The server renders a card for every
 * subscription product in the configured collection; this element:
 *  - shows/hides each card based on whether that product is already in the cart as a
 *    subscription line (matched by variant id). If the cart has no subscription yet,
 *    every card shows (upsell). Once a product is subscribed, its card hides and the
 *    "other" products remain — exactly the spec ("show subscription products not yet
 *    added"). Removing a subscription re-shows its card.
 *  - adds a subscription (variant + selling plan) via /cart/add.js, piggy-backing
 *    `sections` so the theme's <cart-items-component> instances morph, then dispatches
 *    a CartLinesUpdateEvent so the rest of the drawer (count, ship bar, totals) syncs.
 *  - captures the delivery date as a "Delivery Date" line-item property (display format
 *    "D Mon YYYY", matching the PDP date picker) on the /cart/add so a subscription
 *    carries the date to checkout, groups by date, and bills the per-date shipping fee
 *    identically to a one-time purchase. The shipping-fee engine derives the global
 *    "Delivery date" cart attribute from the dated cart lines afterward.
 *
 * Internal cart state is always the /cart.js shape (items / variant_id /
 * selling_plan_allocation / item_count / attributes) — never the Storefront API shape
 * that CartLinesUpdateEvent.createCartFromAjaxResponse produces, which drops those
 * fields. We re-fetch /cart.js after every event for a consistently-shaped cart.
 */
class VoCartOffer extends HTMLElement {
  #connected = false;
  /** @type {any | null} last-known cart JSON (/cart.js shape) */ #cart = null;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#refreshCardRefs();

    // "coming soon" fallback: nothing to wire up when there are no subscription cards
    // AND no one-time upsell add buttons.
    if (!this.#cards.length && !this.querySelector('[data-vo-add-onetime]')) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    // Delegated: survives the cart page's full-mode morph recreating the inner picker.
    this.addEventListener('vo-date-picker:select', this.#onDatePickerSelect);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // Seed cart state, then reflect it in the UI (cards + delivery date).
    this.#seedCart().then(() => this.#syncUIFromCart());
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('vo-date-picker:select', this.#onDatePickerSelect);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;
    const onetimeBtn = target.closest('[data-vo-add-onetime]');
    if (onetimeBtn) {
      this.#onAddOneTime(onetimeBtn);
      return;
    }
    const addBtn = target.closest('[data-vo-add-sub]');
    if (addBtn) {
      this.#onAddSubscription(addBtn);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (target.dataset.voSubOption !== undefined) {
      const card = target.closest('[data-vo-sub-card]');
      if (card) this.#syncVariantSelection(card);
    } else if (target.dataset.voSubPlans !== undefined) {
      this.#onPlanChange(target);
    }
  };

  /** @type {HTMLElement[]} */ #cards = [];
  /** @type {HTMLElement | null} */ #allAddedMsg = null;
  /** Per-card selected delivery date (ISO "YYYY-MM-DD"), keyed by the card element.
   *  Populated by #onDatePickerSelect when the customer picks a date inside a card's
   *  own calendar, and read by #onAddSubscription when they click "Add subscription".
   *  A date pick does NOT mutate the cart — the date is only attached to the
   *  subscription line at add time, so picking a date never adds to the cart. */
  /** @type {Map<HTMLElement, string>} */ #cardDates = new Map();
  /** Parsed per-product variant data embedded by vo-cart-offer-card.liquid. */
  /** @type {WeakMap<HTMLElement, any[]>} */ #cardVariants = new WeakMap();

  /** Rebuild references after a full cart-section morph updates this element's children
   *  while preserving the <vo-cart-offer> host instance. */
  #refreshCardRefs() {
    this.#cards = Array.from(this.querySelectorAll('[data-vo-sub-card]'));
    this.#allAddedMsg = this.querySelector('[data-vo-sub-all-added]');
    this.#cardVariants = new WeakMap();
    this.#cards.forEach((card) => {
      this.#syncVariantSelection(card);
      const picker = card.querySelector('vo-date-picker');
      const grid = picker?.querySelector('.vo-dp-grid');
      if (picker && grid && grid.children.length === 0 && typeof picker.refresh === 'function') {
        picker.refresh();
      }
    });
  }

  /** @param {HTMLElement} card */
  #variantsForCard(card) {
    const cached = this.#cardVariants.get(card);
    if (cached) return cached;
    const data = card.querySelector('[data-vo-sub-variants]');
    try {
      const variants = JSON.parse(data?.textContent || '[]');
      this.#cardVariants.set(card, variants);
      return variants;
    } catch (error) {
      console.warn('[vo-cart-offer] invalid variant data:', error);
      this.#cardVariants.set(card, []);
      return [];
    }
  }

  /** Keep a card's variant, plans, price, image and stock message synchronized. */
  #syncVariantSelection(card) {
    const optionSelects = Array.from(card.querySelectorAll('[data-vo-sub-option]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    const selectedOptions = optionSelects.map((select) => select.value);
    const variants = this.#variantsForCard(card);
    // Values with no in-stock subscribable variant are visible for context but cannot
    // be selected (for example, an entirely sold-out prawn size).
    optionSelects.forEach((select, index) => {
      Array.from(select.options).forEach((option) => {
        option.disabled = !variants.some((item) =>
          item.available && !item.manuallyDisabled && item.plans?.length && item.options?.[index] === option.value
        );
      });
    });
    const variant = variants.find((item) =>
      selectedOptions.length ? item.options?.every((value, index) => value === selectedOptions[index]) : true
    );
    const planSelect = card.querySelector('[data-vo-sub-plans]');
    const button = card.querySelector('[data-vo-add-sub]');
    const status = card.querySelector('[data-vo-sub-availability]');
    const priceEl = card.querySelector('[data-vo-sub-price]');
    const image = card.querySelector('.vo-sub-card__media img');
    if (!(planSelect instanceof HTMLSelectElement) || !(button instanceof HTMLButtonElement)) return;

    const previousPlanId = planSelect.value;
    const plans = variant?.plans ?? [];
    planSelect.replaceChildren(...plans.map((plan) => {
      const option = document.createElement('option');
      option.value = String(plan.id);
      option.dataset.price = plan.price;
      option.textContent = `${plan.name} · ${plan.price}`;
      return option;
    }));
    if (plans.some((plan) => String(plan.id) === previousPlanId)) planSelect.value = previousPlanId;

    const canSubscribe = Boolean(variant?.available && !variant?.manuallyDisabled && plans.length);
    const variantId = canSubscribe ? String(variant.id) : '';
    planSelect.dataset.variantId = variantId;
    button.dataset.variantId = variantId;
    planSelect.disabled = !canSubscribe;
    button.disabled = !canSubscribe;

    if (status) {
      status.hidden = canSubscribe;
      status.textContent = !variant ? 'This option combination is unavailable.'
        : variant.manuallyDisabled ? 'This prawn size is currently unavailable.'
        : !variant.available ? 'Sold out.'
        : !plans.length ? 'No subscription plan is available for this option.' : '';
    }
    if (canSubscribe) {
      this.#onPlanChange(planSelect);
      if (image && variant.image) image.src = variant.image;
    } else if (priceEl) {
      priceEl.textContent = variant?.manuallyDisabled ? 'Unavailable'
        : variant?.available === false ? 'Sold out' : (variant?.price || 'Unavailable');
    }
  }

  /** Format an ISO "YYYY-MM-DD" date as "D Mon YYYY" — the exact display format the PDP
   *  date picker (assets/vo-date-picker.js#formatDisplay) writes and the shipping-fee
   *  engine (assets/shipping-fee-cart.js#parseDate) parses. Subscriptions carry the
   *  delivery date as a line-item property in this format so they group/bill identically
   *  to one-time purchases. Built from explicit y/m/d (not `new Date(iso)`, which parses
   *  as UTC and can shift a day in non-UTC timezones). Returns '' for unparseable input. */
  #formatDisplayDate(iso) {
    if (!iso) return '';
    const parts = iso.split('-').map((n) => parseInt(n, 10));
    if (parts.length !== 3 || !parts.every((n) => Number.isFinite(n) && n > 0)) return '';
    const [y, m, d] = parts;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dt = new Date(y, m - 1, d);
    return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
  }

  /** @returns {string[]} section ids of every cart-items-component on the page */
  #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
    });
    return Array.from(ids);
  }

  /**
   * POST to a cart AJAX endpoint with `sections` piggy-backed, returning parsed JSON.
   * @param {string} url
   * @param {Record<string, unknown>} body
   */
  async #cartFetch(url, body) {
    const payload = {
      ...body,
      sections: this.#sectionIds().join(','),
      sections_url: window.location.pathname,
    };
    const res = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    const text = await res.text();
    return JSON.parse(text);
  }

  /** Resolve the deferred CartLinesUpdateEvent promise with the fresh cart + sections.
   * @param {any} parsed The AJAX response (carries `sections`; for /cart/add.js it carries
   *   only the added line in `items` and NO `item_count`).
   * @param {string} action
   * @param {any[]} lines
   * @param {any | null} [fullCart] The full /cart.js cart. When supplied (after /cart/add),
   *   used for `items` + `item_count` + the Storefront cart so subscribers see the WHOLE
   *   cart — /cart/add.js's response only describes the line just added, not the cart. */
  #dispatchCartUpdate(parsed, action, lines = [], fullCart = null, source = 'vo-cart-offer') {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
    );
    if (parsed && parsed.errors) {
      deferred.reject(new Error(typeof parsed.errors === 'string' ? parsed.errors : 'Cart update failed'));
      return deferred.promise;
    }
    // /cart/add.js returns only the added line in `items` and omits `item_count`, so when
    // we have a full /cart.js fetch prefer it for everything subscribers read. The SRA
    // `sections` always come from the AJAX response itself (the piggy-backed render of
    // the page at sections_url), never from /cart.js.
    const cart = fullCart || parsed;
    const storefrontCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    this.#cart = cart;
    deferred.resolve({
      cart: storefrontCart,
      detail: {
        sections: parsed?.sections ?? {},
        items: cart?.items ?? [],
        itemCount: cart?.item_count ?? 0,
        source,
        didError: false,
      },
    });
    return deferred.promise;
  }

  /**
   * Shopify can occasionally return bundled section HTML from /cart/add.js that is one
   * cart revision behind the /cart.js response fetched immediately afterward. The event
   * is still valid in that case, but morphing that HTML leaves the newly added line out
   * until a reload (or a later shipping-fee update) happens. Compare the rendered line
   * keys with the confirmed full cart and refetch only the sections that disagree.
   *
   * @param {any | null} cart Full /cart.js response after the add.
   */
  async #ensureCartSectionsFresh(cart) {
    if (!cart?.items) return;

    const merchandiseCount = cart.items.reduce(
      (sum, item) =>
        String(item?.properties?._shipping_fee) === 'true' ? sum : sum + (Number(item?.quantity) || 0),
      0
    );
    const syncMerchandiseCount = () => {
      document.querySelectorAll('[data-vo-cart-count]').forEach((count) => {
        const next = String(merchandiseCount);
        if (count.textContent !== next) count.textContent = next;
      });
    };

    // The shipping-fee reconciliation runs independently and may publish one more
    // section response shortly after the add. Guard the count during that brief window:
    // MutationObserver callbacks run before paint, so an older response cannot flash 0
    // while the newly constructed drawer controller performs its own /cart.js seed.
    const sectionRoots = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.closest('.shopify-section'))
      .filter((root, index, roots) => root && roots.indexOf(root) === index);
    const countObserver = new MutationObserver(syncMerchandiseCount);
    sectionRoots.forEach((root) => countObserver.observe(root, { childList: true, subtree: true, characterData: true }));
    setTimeout(() => countObserver.disconnect(), 4000);
    syncMerchandiseCount();

    const expectedKeys = new Set(
      cart.items
        .filter((item) => String(item?.properties?._shipping_fee) !== 'true')
        .map((item) => String(item.key || ''))
        .filter(Boolean)
    );

    const staleComponents = Array.from(document.querySelectorAll('cart-items-component')).filter((component) => {
      if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return false;
      const renderedKeys = new Set(
        Array.from(component.querySelectorAll('tr.cart-items__table-row[data-key]'))
          .map((row) => row.dataset.key || '')
          .filter(Boolean)
      );
      return (
        renderedKeys.size !== expectedKeys.size ||
        Array.from(expectedKeys).some((key) => !renderedKeys.has(key))
      );
    });

    await Promise.all(
      staleComponents.map((component) =>
        sectionRenderer.renderSection(component.dataset.sectionId, {
          cache: false,
          mode: component.dataset.drawer !== undefined ? 'hydration' : 'full',
        })
      )
    );

    syncMerchandiseCount();
  }

  async #seedCart() {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`);
      this.#cart = await res.json();
      console.log('[vo-cart-offer] seedCart', {
        itemCount: this.#cart?.item_count,
        attributes: this.#cart?.attributes,
      });
    } catch (error) {
      console.warn('[vo-cart-offer] seed cart failed:', error);
    }
  }

  /** Fetch the full /cart.js cart. Returns null on failure so callers can fall back. */
  async #fetchCartJson() {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`);
      return await res.json();
    } catch (error) {
      console.warn('[vo-cart-offer] fetch cart failed:', error);
      return null;
    }
  }

  #onCartLinesUpdate = async (event) => {
    try {
      await event.promise;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[vo-cart-offer] cart event rejected:', error);
    }
    // Other cart listeners morph on the same resolved promise. Wait until the next frame,
    // then repair runtime-generated controls if a full morph preserved this host but
    // replaced its children with server markup.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.#refreshCardRefs();

    // resolved.cart is the Storefront API shape; re-fetch /cart.js for the shape this
    // class reads (items / variant_id / selling_plan_allocation / attributes).
    await this.#seedCart();
    this.#syncUIFromCart();
  };

  /** Set of variant ids currently in the cart as a subscription line. */
  #subscribedVariantIds() {
    const set = new Set();
    for (const item of this.#cart?.items ?? []) {
      if (item.selling_plan_allocation) set.add(String(item.variant_id));
    }
    return set;
  }

  /** Show/hide offer cards based on the real cart. Hides a card if its product is
   * already subscribed (any of its variant ids is in the cart on a selling plan). */
  #syncUIFromCart() {
    const subscribed = this.#subscribedVariantIds();
    let visible = 0;
    for (const card of this.#cards) {
      const ids = (card.dataset.variantIds || '').split(',').filter(Boolean);
      const isSubscribed = ids.some((id) => subscribed.has(id));
      card.hidden = isSubscribed;
      if (!isSubscribed) visible += 1;
    }
    if (this.#allAddedMsg) this.#allAddedMsg.hidden = visible > 0;

    // Per-card delivery dates are chosen inside each card's own calendar and read at
    // "Add subscription" time (#onAddSubscription via #cardDates). Nothing to sync here:
    // a card's calendar is hidden/shown with the card itself (card.hidden above), and
    // each picker self-initialises from its initial_date_iso (the saved "Delivery date"
    // cart attribute, or the first available month).
  }

  // ---- User actions ----

  #onPlanChange(select) {
    const option = select.selectedOptions[0];
    const priceEl = select.closest('[data-vo-sub-card]')?.querySelector('[data-vo-sub-price]');
    if (option && priceEl) {
      priceEl.innerHTML = `${option.dataset.price}<small> / delivery</small>`;
    }
  }

  async #onAddSubscription(button) {
    const card = button.closest('[data-vo-sub-card]');
    const variantId = button.dataset.variantId;
    const select = card?.querySelector('[data-vo-sub-plans]');
    const planId = select?.value;
    const selectedVariant = card instanceof HTMLElement && variantId
      ? this.#variantsForCard(card).find((variant) => String(variant.id) === String(variantId))
      : null;
    if (selectedVariant?.manuallyDisabled) {
      if (card instanceof HTMLElement) this.#syncVariantSelection(card);
      return;
    }
    if (!variantId || !planId) {
      // No frequency chosen — surface a prompt instead of failing silently so the
      // click is never a no-op (the historic "nothing happens" symptom).
      this.#returnToItems('Pick a delivery frequency, then add your subscription');
      return;
    }
    const picker = card?.querySelector('vo-date-picker');
    const isoDate = this.#cardDates.get(card) || picker?.selectedDate || '';
    if (!isoDate || typeof picker?.isDateSelectable !== 'function' || !picker.isDateSelectable(isoDate)) {
      picker?.showValidationError?.('Please select an available delivery date.');
      return;
    }
    button.disabled = true;
    // Immediately return to Step 1 and show its loading overlay while the /cart/add is in
    // flight, so the customer sees instant feedback instead of a frozen Step 2. This
    // element's CartLinesUpdateEvent fires only AFTER the add resolves, so the drawer's own
    // cart-lines listener can't span the fetch — drive the overlay from here. The drawer
    // clears it when the add resolves; the finally below is a backstop for the no-drawer /
    // failure cases.
    const drawer = this.closest('vo-cart-drawer') || document.querySelector('vo-cart-drawer');
    if (drawer) { drawer.gotoStep(1); drawer.setLoading(true); }
    try {
      // Capture THIS card's validated delivery date as a line-item property in the SAME
      // "D Mon YYYY" display format
      // the PDP date picker (assets/vo-date-picker.js#formatDisplay) writes for one-time
      // purchases. This is what makes a subscription flow to checkout, group by date, and
      // bill the per-date shipping fee identically to a one-time purchase — the
      // shipping-fee engine (assets/shipping-fee-cart.js#parseDate) parses "15 Jun 2025",
      // not ISO, so the format must match exactly. That engine also derives the global
      // ISO "Delivery date" cart attribute from the actual dated cart lines.
      // The date the customer expects is the one HIGHLIGHTED in the card's own calendar
      // — either a day they clicked (recorded in #cardDates) or, if they never clicked,
      // the picker's pre-selected initial date (the saved "Delivery date" cart attribute,
      // which already respects the calendar's disabled-day / weekend / cutoff rules).
      const displayDate = this.#formatDisplayDate(isoDate);

      const lineItem = { id: Number(variantId), quantity: 1, selling_plan: Number(planId) };
      if (displayDate) lineItem.properties = { 'Delivery Date': displayDate };

      const parsed = await this.#cartFetch(Theme.routes.cart_add_url, { items: [lineItem] });
      if (parsed && parsed.errors) {
        throw new Error(typeof parsed.errors === 'string' ? parsed.errors : 'Cart rejected the subscription');
      }

      // /cart/add.js returns ONLY the added line in `items` and omits `item_count`, so
      // re-fetch /cart.js for the full cart the drawer's count + free-ship bar need.
      // (product-form's add flow does the same re-fetch.) Non-blocking if it fails —
      // the dispatch + morph below still run; the drawer falls back to its own seed.
      const fullCart = await this.#fetchCartJson();

      // `add` events require a ProductVariant GID in `merchandiseId`. Passing the
      // AJAX `id` shape here makes Shopify's standard-event validator reject the
      // event, so the backend cart changes but the cart section never morphs.
      await this.#dispatchCartUpdate(
        parsed,
        'add',
        [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        fullCart
      );

      // The bundled section response can lag behind the confirmed full cart. Repair
      // only an actual mismatch so a normal add incurs no extra section request.
      await this.#ensureCartSectionsFresh(fullCart);

      // The cart section listeners are attached to the same resolved promise. Give
      // their morph work one frame to finish before removing the loading state and
      // announcing the updated cart.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // Re-assert Step 1 after the cart morph and announce the completed add. The
      // drawer was already moved to Step 1 before the request, so this also covers
      // a full cart-page morph that recreated its inner step markup.
      this.#returnToItems('Subscription added to your cart');

      // Optimistically hide the card now subscribed; #onCartLinesUpdate re-syncs.
      card.hidden = true;
      if (this.#allAddedMsg) {
        this.#allAddedMsg.hidden = this.#cards.some((c) => !c.hidden);
      }
    } catch (error) {
      console.error('[vo-cart-offer] add subscription failed:', error);
      if (card) card.hidden = false;
      // Surface the failure so a rejected /cart/add is never a silent no-op.
      this.#returnToItems('Couldn’t add the subscription — please try again');
    } finally {
      button.disabled = false;
      if (drawer) drawer.setLoading(false);
    }
  }

  /** Add a one-time upsell product (no selling plan) from the Offer step. Qty 1,
   *  sharing the cart's current delivery date when one exists so the shipping-fee
   *  engine groups the add-on with the order. Dispatches with a distinct source
   *  ('vo-cart-upsell') so the drawer doesn't announce the subscription message. */
  async #onAddOneTime(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    button.disabled = true;
    const drawer = this.closest('vo-cart-drawer') || document.querySelector('vo-cart-drawer');
    if (drawer) { drawer.gotoStep(1); drawer.setLoading(true); }
    try {
      // Carry the cart's existing Delivery/Harvest Date (display format) so the add-on
      // bills the same per-date shipping fee as the rest of the order.
      const props = {};
      const src = (this.#cart?.items ?? []).find(
        (i) => i.properties && (i.properties['Delivery Date'] || i.properties['Harvest Date'])
      );
      if (src) {
        const d = src.properties['Delivery Date'] || src.properties['Harvest Date'];
        if (d) props['Delivery Date'] = d;
      }
      const lineItem = { id: Number(variantId), quantity: 1 };
      if (Object.keys(props).length) lineItem.properties = props;

      const parsed = await this.#cartFetch(Theme.routes.cart_add_url, { items: [lineItem] });
      if (parsed && parsed.errors) {
        throw new Error(typeof parsed.errors === 'string' ? parsed.errors : 'Cart rejected the add-on');
      }
      const fullCart = await this.#fetchCartJson();
      await this.#dispatchCartUpdate(
        parsed,
        'add',
        [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        fullCart,
        'vo-cart-upsell'
      );
      await this.#ensureCartSectionsFresh(fullCart);
      this.#returnToItems('Added to your cart');
    } catch (error) {
      console.error('[vo-cart-offer] add one-time upsell failed:', error);
      this.#returnToItems('Couldn’t add the item — please try again');
    } finally {
      button.disabled = false;
      if (drawer) drawer.setLoading(false);
    }
  }

  /** @param {CustomEvent & { detail?: { iso?: string } }} event */
  #onDatePickerSelect = (event) => {
    const iso = event.detail?.iso || '';
    const picker = event.target;
    const card = picker?.closest?.('[data-vo-sub-card]');
    if (card) this.#cardDates.set(card, iso);
    // Intentionally NO cart mutation here: picking a date only records the choice for
    // this card. The date is attached to the subscription when the customer clicks
    // "Add subscription" (#onAddSubscription), so a date pick never adds to the cart.
  };

  /**
   * Send the user back to Step 1 (Items) and surface a success notice above the
   * free-shipping bar. The <vo-cart-offer> lives inside <vo-cart-drawer>, so the
   * drawer is reachable via closest(). Falls back to a document query for the
   * (rare) case where the offer renders outside the drawer chrome.
   * @param {string} message
   */
  #returnToItems(message) {
    const drawer = this.closest('vo-cart-drawer') || document.querySelector('vo-cart-drawer');
    if (!drawer) return;
    if (typeof drawer.gotoStep === 'function') drawer.gotoStep(1);
    if (typeof drawer.showNotice === 'function') drawer.showNotice(message);
  }

}

if (!customElements.get('vo-cart-offer')) {
  customElements.define('vo-cart-offer', VoCartOffer);
}
